import {
  ImportWarning,
  ImportWarningCode,
  UnmappedColumnsPayload,
} from "types/dataImport.types"

const KNOWN_CODES: readonly string[] = [
  "missing",
  "convert",
  "case",
  "rename",
  "related",
  "unmapped",
  "dropped",
  "datamap",
]

const PREFIX_PATTERN = /^\[([a-z]+)\]\s*/

/**
 * Splits the engine's newline-joined import messages into typed entries.
 *
 * The engine tags each message with a machine-readable `[code] ` prefix. A line with an
 * unrecognised prefix, or none at all, is kept verbatim as "other" — so messages from an
 * engine build that predates the prefixes still render correctly.
 *
 * @param raw The newline-joined `warnings` string from the combine-data response.
 * @returns One entry per non-empty line, in the order the engine emitted them.
 */
export const parseImportWarnings = (raw?: string): ImportWarning[] => {
  if (!raw) return []

  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => {
      const match = PREFIX_PATTERN.exec(line)

      if (match && KNOWN_CODES.includes(match[1])) {
        return { code: match[1] as ImportWarningCode, text: line.slice(match[0].length) }
      }

      return { code: "other" as ImportWarningCode, text: line }
    })
}

const ATTENTION_ORDER: Record<string, number> = { dropped: 0, datamap: 1 }

/**
 * Splits parsed warnings into the two sections the import panel renders: items worth acting
 * on, and the aggregated inventory of columns that were not imported.
 */
export const groupImportWarnings = (
  warnings: ImportWarning[],
): { attention: ImportWarning[]; informational: ImportWarning[] } => ({
  // A dropped question loses data; a datamap problem explains why. Both outrank the rest.
  attention: warnings
    .filter((warning) => warning.code !== "unmapped")
    .map((warning, index) => ({ warning, index }))
    .sort((a, b) => (ATTENTION_ORDER[a.warning.code] ?? 2) - (ATTENTION_ORDER[b.warning.code] ?? 2) || a.index - b.index)
    .map(({ warning }) => warning),
  informational: warnings.filter((warning) => warning.code === "unmapped"),
})

const isRelatedColumn = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { column?: unknown }).column === "string" &&
  typeof (value as { relatedTo?: unknown }).relatedTo === "string"

/**
 * Reads the JSON inventory out of an `[unmapped]` message.
 *
 * The engine emits the full column list as JSON because prose cannot carry eighty-odd names
 * plus their grouping. Returns null when the body is not the expected shape — an older engine
 * build emits a prose sentence here, and a non-UTF-8 header row could in principle produce
 * bytes that will not parse. Callers fall back to showing the raw text.
 */
export const parseUnmappedPayload = (text: string): UnmappedColumnsPayload | null => {
  try {
    const parsed: unknown = JSON.parse(text)

    if (typeof parsed !== "object" || parsed === null) return null

    const { count, related, other } = parsed as Record<string, unknown>

    if (typeof count !== "number") return null
    if (!Array.isArray(related) || !related.every(isRelatedColumn)) return null
    if (!Array.isArray(other) || !other.every((name) => typeof name === "string")) return null

    return parsed as unknown as UnmappedColumnsPayload
  } catch {
    return null
  }
}

/**
 * How many columns were left out of the import, across every `[unmapped]` message.
 *
 * Falls back to the leading integer of the message when the body is not JSON, so an engine
 * build that predates the payload change still shows a sensible count.
 */
export const unmappedColumnCount = (warnings: ImportWarning[]): number =>
  warnings
    .filter((warning) => warning.code === "unmapped")
    .reduce((total, warning) => {
      const payload = parseUnmappedPayload(warning.text)
      return total + (payload ? payload.count : Number.parseInt(warning.text, 10) || 0)
    }, 0)
